<section class="page-surface p-6 lg:p-8">
  <div
    *ngIf="duplicateModalMessage"
    class="fixed inset-0 z-50 flex items-center justify-center px-4"
    role="dialog"
    aria-modal="true"
  >
    <div class="absolute inset-0 bg-slate-900/40" (click)="closeDuplicateModal.emit()"></div>
    <div class="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-lg font-semibold text-slate-800">Duplicate Director Detected</h3>
          <p class="mt-2 text-sm text-slate-600">{{ duplicateModalMessage }}</p>
        </div>
        <button
          type="button"
          class="page-btn border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          (click)="closeDuplicateModal.emit()"
          aria-label="Close duplicate director modal"
        >
          Close
        </button>
      </div>
    </div>
  </div>

  <div class="flex flex-wrap items-center justify-between gap-4">
    <h2 class="text-lg font-semibold text-slate-700 underline underline-offset-4">
      Director Information
    </h2>

    <button type="button" class="page-btn page-btn-secondary" (click)="addDirector.emit()">
      Add Director
    </button>
  </div>

  <div class="mt-6 space-y-8">
    <div
      class="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6"
      *ngFor="let group of directors.controls; let i = index; trackBy: trackByIndex"
      [formGroup]="group"
    >
      <div class="flex items-center justify-between gap-4">
        <h3 class="text-lg font-semibold text-slate-700 underline underline-offset-4">
          Director {{ i + 1 }}
        </h3>

        @if (directors.controls.length > 1) {
          <button
            type="button"
            class="page-btn border border-red-200 bg-white text-red-600 hover:bg-red-50"
            (click)="removeDirector.emit(i)"
          >
            Remove
          </button>
        }
      </div>

      <div class="mt-6 grid gap-5 lg:grid-cols-2">
        <div class="page-control-group">
          <label class="page-label" [for]="'directorAsin' + i"
            >ASIN <span class="ml-1 text-red-500">*</span></label
          >
          <div class="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
            <input
              class="page-control"
              [id]="'directorAsin' + i"
              type="text"
              placeholder="e.g. 104268290"
              formControlName="asin"
              [class.border-red-300]="hasGroupError(group, 'asin')"
            />
            <button
              type="button"
              class="page-btn page-btn-secondary h-[46px] px-6"
              (click)="verifyAsin.emit(i)"
              [disabled]="busyVerify[i]"
            >
              @if(busyVerify[i]) {
                <div class="flex items-center gap-2">
                  <span
                    class="inline-block size-6 animate-spin rounded-full border-4 border-slate-400/80 border-t-white"
                    aria-hidden="true"></span>
                  <p>
                    Verifying...
                  </p>
                </div>
              } @else {
                Verify Asin
              }
            </button>
          </div>
          <small *ngIf="hasGroupError(group, 'asin')" class="text-xs text-red-500"
            >ASIN is required.</small
          >
          <small *ngIf="hasGroupError(group, 'asinVerified')" class="text-xs text-red-500"
            >Please verify ASIN.</small
          >
        </div>

        <div class="page-control-group">
          <label class="page-label" [for]="'fullName' + i">
            Full Name <span class="ml-1 text-red-500">*</span>
            <span
              *ngIf="isAsinVerified(group)"
              class="ml-2 inline-flex items-center gap-1 text-emerald-700"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <circle cx="12" cy="12" r="9"></circle>
                <path d="m9 12 2 2 4-4"></path>
              </svg>
              <span class="text-xs font-medium">Verified</span>
            </span>
          </label>
          <input
            class="page-control"
            [id]="'fullName' + i"
            type="text"
            placeholder="e.g. John Smith"
            formControlName="fullName"
            [class.border-red-300]="hasGroupError(group, 'fullName')"
          />
          <small *ngIf="hasGroupError(group, 'fullName')" class="text-xs text-red-500"
            >Full name is required.</small
          >
        </div>

        <div class="page-control-group">
          <label class="page-label" [for]="'directorPhone' + i"
            >Phone Number <span class="ml-1 text-red-500">*</span></label
          >
          <input
            [appCharLength]="11"
            class="page-control"
            [id]="'directorPhone' + i"
            type="text"
            placeholder="07063352881"
            formControlName="phoneNumber"
            [class.border-red-300]="hasGroupError(group, 'phoneNumber')"
          />
          <small *ngIf="hasGroupError(group, 'phoneNumber')" class="text-xs text-red-500"
            >Phone number must be 11 digits.</small
          >
        </div>

        <div class="page-control-group">
          <label class="page-label" [for]="'directorEmail' + i"
            >Email <span class="ml-1 text-red-500">*</span></label
          >
          <input
            [appCharLength]
            class="page-control"
            [id]="'directorEmail' + i"
            type="email"
            placeholder="example@gmail.com"
            formControlName="email"
            [class.border-red-300]="hasGroupError(group, 'email')"
          />
          <small *ngIf="hasGroupError(group, 'email')" class="text-xs text-red-500"
            >Enter a valid email address.</small
          >
        </div>

        <div class="page-control-group">
          <label class="page-label" [for]="'directorAddress' + i"
            >Address <span class="ml-1 text-red-500">*</span></label
          >
          <input
            class="page-control"
            [id]="'directorAddress' + i"
            type="text"
            placeholder="12 parakou street wuse 2 abuja"
            formControlName="address"
            [class.border-red-300]="hasGroupError(group, 'address')"
          />
          <small *ngIf="hasGroupError(group, 'address')" class="text-xs text-red-500"
            >Address is required.</small
          >
        </div>

        <div class="page-control-group">
          <label class="page-label" [for]="'directorBvn' + i"
            >BVN <span class="ml-1 text-red-500">*</span></label
          >
          <input
            [appCharLength]="11"
            class="page-control"
            [id]="'directorBvn' + i"
            type="text"
            placeholder="e.g. 22334455667"
            formControlName="bvn"
            [class.border-red-300]="hasGroupError(group, 'bvn')"
          />
          <small *ngIf="hasGroupError(group, 'bvn')" class="text-xs text-red-500"
            >BVN must be 11 digits.</small
          >
        </div>
      </div>
    </div>
  </div>
</section>
